import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Moon, Sun, Monitor, type LucideIcon } from 'lucide-react';
import { applyTheme, getTheme, onThemeChange, type ThemeMode } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { COARSE_HIT_ALONE } from '@/lib/touch-target';

const NEXT: Record<ThemeMode, ThemeMode> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};

const ICON: Record<ThemeMode, LucideIcon> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};


interface ThemeToggleProps {
  className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps) {
  const [theme, setTheme] = useState<ThemeMode>(() => getTheme());
  const { t } = useTranslation('shell');
  // A literal key per mode so the extractor sees all three (#2109).
  const label =
    theme === 'light' ? t('themeToggle.light') : theme === 'dark' ? t('themeToggle.dark') : t('themeToggle.system');

  useEffect(() => onThemeChange(setTheme), []);

  useEffect(() => {
    if (theme !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme('system');
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [theme]);

  const cycle = () => applyTheme(NEXT[theme]);

  const Icon = ICON[theme];

  return (
    <button
      type="button"
      onClick={cycle}
      aria-label={t('themeToggle.aria', { current: label })}
      title={label}
      className={cn(
        'inline-flex h-9 w-9 items-center justify-center rounded-full text-stevens-ink-2 transition-colors hover:bg-stevens-bg-2 hover:text-stevens-ink',
        // #2603 (D-2420): 36 px drawn, 44 px reach on a coarse pointer. It
        // stands alone in the public top bar, so the hit area needs no -z-10.
        COARSE_HIT_ALONE,
        '[@media(pointer:coarse)]:after:-inset-1',
        className,
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
